import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from '@/stores/main';
import { createConnectionLifecycle, type ConnectionStatus } from '@/utils/connectionLifecycle';
import { configureRealtimeSession, listen, onProfileSubscribed, pauseRealtime, resumeRealtime } from '@/composables/useRealtime';

export function useAppConnection() {
  const store = useStore();
  const status = ref<ConnectionStatus>('idle');
  let lifecycle: ReturnType<typeof createConnectionLifecycle> | null = null;
  let unsubscribe: (() => void) | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let remoteRevision = 0;
  let remoteProfileId: number | null = null;
  let syncing: Promise<void> | null = null;
  let syncVersion = 0;
  let catchupRequested = false;
  let sessionGeneration = 0;
  let stopped = false;

  function canSync() {
    return !stopped && store.loggedIn && Boolean(store.token) &&
      Boolean(store.getActiveProfileId()) && !document.hidden && navigator.onLine;
  }

  function needsRefresh() {
    return catchupRequested || (remoteProfileId === store.getActiveProfileId() &&
      remoteRevision > Number(store.profile?.answer_revision || 0));
  }

  async function syncAnswers() {
    syncVersion++;
    if (syncing) return syncing;
    if (!canSync()) return;
    const context = store.getProfileRequestContext();
    const generation = sessionGeneration;
    const current = () => canSync() && generation === sessionGeneration && store.isProfileRequestCurrent(context);
    const request = (async () => {
      while (current()) {
        await store.whenAnswerSavesSettled();
        if (!current()) return;
        const hadPendingAnswers = store.readAnswerOutbox().length > 0;
        await store.flushAnswerOutbox();
        await store.whenAnswerSavesSettled();
        if (!current()) return;
        // Keep catch-up intent if a new foreground save or an offline queue blocks the read.
        catchupRequested ||= hadPendingAnswers;
        if (store.pendingAnswerSaves > 0) return;

        const version = syncVersion;
        if (needsRefresh()) {
          const forced = catchupRequested;
          catchupRequested = false;
          const refreshed = await store.reconcileCurrentProfile(true);
          if (!current()) return;
          if (!refreshed) catchupRequested ||= forced;
        }
        // Only an actual event received during this pass requests another pass. No polling.
        if (version === syncVersion) return;
      }
    })().catch(() => {
      // Preserve local drafts and position when synchronization is unavailable.
    }).finally(() => { if (syncing === request) syncing = null; });
    syncing = request;
    return request;
  }

  function scheduleSync() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void syncAnswers();
    }, 350);
  }

  function recordRevision(profileId: number, revision: number) {
    if (profileId !== store.getActiveProfileId() || !Number.isFinite(revision) || revision < 0) return;
    remoteProfileId = profileId;
    remoteRevision = Math.max(remoteRevision, revision);
  }

  function refreshAfterEvent(payload: { profile_id?: number; answer_revision?: number }) {
    if (Number(payload.profile_id) !== store.getActiveProfileId()) return;
    recordRevision(Number(payload.profile_id), Number(payload.answer_revision) || 0);
    scheduleSync();
  }

  // Subscription success closes the HTTP-probe/socket gap, including the first subscription.
  // It shares the tab probe's sync queue: one post-subscription read, not one per listener/AI session.
  const stopSubscriptionWatch = onProfileSubscribed((profileId) => {
    if (profileId !== store.getActiveProfileId() || !canSync()) return;
    catchupRequested = true;
    scheduleSync();
  });
  const stopPendingWatch = watch(() => store.pendingAnswerSaves, (count, previous) => {
    if (previous > 0 && count === 0 && needsRefresh() && canSync()) scheduleSync();
  });

  const stopSessionWatch = watch([() => store.loggedIn, () => store.token, () => store.userId, () => store.getActiveProfileId()], () => {
    sessionGeneration++;
    syncing = null;
    catchupRequested = false;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    unsubscribe?.();
    unsubscribe = null;
    remoteRevision = 0;
    remoteProfileId = null;
    configureRealtimeSession(store.loggedIn ? store.token : '', store.loggedIn ? store.userId : 0);
    if (store.loggedIn && store.getActiveProfileId()) {
      unsubscribe = listen(`profile.${store.getActiveProfileId()}`, 'profile.answers-updated', refreshAfterEvent);
      if (!document.hidden) resumeRealtime();
    } else {
      pauseRealtime();
    }
    status.value = 'idle';
  }, { immediate: true });

  onMounted(() => {
    lifecycle = createConnectionLifecycle({
      document, window,
      isAuthenticated: () => store.loggedIn && Boolean(store.token),
      isOnline: () => navigator.onLine,
      async check(signal) {
        const generation = sessionGeneration;
        const requestToken = store.token;
        const requestUser = store.userId;
        const profileId = store.getActiveProfileId();
        const current = () => !stopped && !signal.aborted && store.loggedIn &&
          generation === sessionGeneration && requestToken === store.token &&
          requestUser === store.userId && profileId === store.getActiveProfileId();
        const url = new URL(`${import.meta.env.APP_BACKEND_API_ENDPOINT.replace(/\/$/, '')}/connection-status`);
        if (profileId) url.searchParams.set('profile_id', String(profileId));
        const response = await fetch(url, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${requestToken}` },
          credentials: 'include', cache: 'no-store', signal,
        });
        if (!current()) return 'idle';
        if (response.status === 401) return 'expired';
        if (!response.ok) return 'unavailable';
        const payload = await response.json();
        if (!current()) return 'idle';
        if (Number(payload.user_id) !== requestUser) return 'expired';
        recordRevision(Number(payload.profile?.id), Number(payload.profile?.answer_revision) || 0);
        return 'online';
      },
      onStatus: (value) => { status.value = value; },
      onHidden() {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = null;
        pauseRealtime();
      },
      onReady() {
        resumeRealtime();
        void syncAnswers();
      },
    });
  });

  onUnmounted(() => {
    stopped = true;
    sessionGeneration++;
    lifecycle?.stop();
    stopSessionWatch();
    stopPendingWatch();
    stopSubscriptionWatch();
    unsubscribe?.();
    if (refreshTimer) clearTimeout(refreshTimer);
  });
  return { status, retry: () => lifecycle?.check() };
}
